import React, { useEffect, useMemo, useState } from "react";
import { Clock, ForkKnife, Translate, CurrencyCircleDollar } from "@phosphor-icons/react";
import api from "../../lib/api";
import { getUserId } from "../../lib/user";
import { formatMoney } from "../../lib/currency";
import { getCountry } from "../../lib/tipping";
import BrandMark from "./BrandMark";
import AccountButton from "../AccountButton";

/**
 * Recents tab — chronological feed of the last things the user did:
 * saved tips, recent translations (from localStorage cache), recent
 * currency conversions.
 *
 * Phase 1 implementation: shows the 10 most-recent saved tips as a
 * stub. Translation + currency history will be wired in Phase 4 when
 * we add the local activity log.
 */
export default function RecentsTab({ openPaywall }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const userId = useMemo(() => getUserId(), []);

  useEffect(() => {
    let live = true;
    (async () => {
      try {
        const r = await api.get(`/tips`, { params: { user_id: userId } });
        const recent = (r.data.items || [])
          .map((t) => ({
            kind: "tip",
            id: t.id,
            at: t.created_at,
            entry: t,
          }))
          .sort((a, b) => new Date(b.at) - new Date(a.at))
          .slice(0, 10);
        if (live) setItems(recent);
      } finally {
        if (live) setLoading(false);
      }
    })();
    return () => {
      live = false;
    };
  }, [userId]);

  return (
    <div className="px-5 pt-4" data-testid="recents-screen">
      <div className="flex items-center justify-between mb-5">
        <BrandMark />
        <AccountButton openPaywall={() => openPaywall?.()} />
      </div>

      <h1 className="font-jakarta text-5xl font-extrabold leading-none tracking-tight text-navy mb-2">
        Recent <span className="font-jakarta font-extrabold text-orange">Activity</span>
      </h1>
      <p className="text-navy-soft text-sm font-medium mb-6">
        The last 10 things you did in TipLingo.
      </p>

      {loading && (
        <div className="text-center text-navy-soft py-12 text-sm">Loading…</div>
      )}

      {!loading && items.length === 0 && (
        <div className="text-center bg-white rounded-2xl shadow-soft border border-navy/5 py-10 px-6">
          <div className="font-jakarta font-extrabold text-navy text-xl mb-1">
            Nothing yet
          </div>
          <div className="text-navy-soft text-sm">
            Save a tip, translate a phrase, or convert a currency to see it
            show up here.
          </div>
        </div>
      )}

      <div className="space-y-3 pb-2">
        {items.map((it) => (
          <RecentRow key={it.id} item={it} />
        ))}
      </div>
    </div>
  );
}

function RecentRow({ item }) {
  const e = item.entry;
  const country = e.country_code ? getCountry(e.country_code) : null;
  const flag = country?.flag || "🍽️";
  const date = e.created_at
    ? new Date(e.created_at).toLocaleDateString(undefined, {
        month: "short",
        day: "numeric",
      })
    : "";
  return (
    <div className="bg-white rounded-2xl shadow-soft border border-navy/5 p-4 flex items-center gap-3">
      <div className="shrink-0 w-12 h-12 rounded-2xl bg-orange-soft flex items-center justify-center">
        <ForkKnife size={22} weight="fill" color="#E55B2C" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-navy-soft text-xs font-semibold flex items-center gap-1.5">
          <span className="text-base">{flag}</span>
          {country?.name || "Tip"} • {e.currency || "USD"}
        </div>
        <div className="text-navy font-semibold text-base leading-tight">
          Saved tip
        </div>
        <div className="text-navy-soft text-xs mt-0.5">
          {e.tip_pct ?? "—"}% • {e.people || 1}{" "}
          {(e.people || 1) === 1 ? "person" : "people"}
        </div>
      </div>
      <div className="shrink-0 text-right">
        <div className="text-navy-soft text-[10px] uppercase tracking-wider">
          {date}
        </div>
        <div className="font-jakarta font-extrabold text-navy text-lg">
          {formatMoney(e.total || 0, e.currency || "USD")}
        </div>
      </div>
    </div>
  );
}
